import { useMemo } from "react";
import { genericEntityConstants, GenericEntityConstantsEntities } from "../../../api/GenericConstants";
import ContentRow, { ContentRowProps } from "../../../common/tables/ContentRow/ContentRow";
import { NotAllowed, NotSet } from "../../../common/misc/UIconstants";
import { DateTimeRenderer } from "../../../common/datetime/DateTimeFormatter";
import { GetPersons } from "../../../common/misc/EntityRenders/EntityRenderer";
import { toUppercase } from "../../../common/helperfunctions/stringFunctions";
import { GenericEntity, GenericEntityComponentProps } from "../GenericComponents";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { useEntityPermissions } from "../../../common/permissions/useEntityPermissions";
import { getNotebookEntryStringLabel } from "../../../ELN/LabNotebookEntry/LabNotebookEntry";
import { LabNotebookEntry } from "../../../ELN/types/LabNotebookEntry";
import { AuditLog } from "../../../api/AuditLog";
import { eventIdToInfo } from "../../../AuditLog/AuditLog";

export const GenericEntityContentRow = <Entity extends GenericEntity>({
  entityTypeId,
  entity,
  componentProps,
}: GenericEntityComponentProps<Entity, ContentRowProps>) => {
  const suggestedEntityProps = useDefaultGenericEntityContentRow({ entityTypeId, entity });
  const defaultComponentProps = useMemo(() => {
    const { entityConstants } = genericEntityConstants[entityTypeId];
    return {
      // Generic values
      icon: entityConstants.icon ? <LucideIcon name={entityConstants.icon} color="var(--primary)" /> : undefined,
      id:
        !!entity?.id && entityConstants?.entitySingular
          ? `${toUppercase(entityConstants.entitySingular)}-ID: ${entity.id}`
          : NotSet,
      label: entity?.name ?? NotSet,
      date:
        !!entity && Object.hasOwn(entity ?? {}, "modifiedOn") ? <DateTimeRenderer date={entity.modifiedOn} /> : NotSet,
      info:
        !!entity && Object.hasOwn(entity ?? {}, "modifiedBy") ? (
          <GetPersons persons={entity.modifiedBy} createLinks={false} />
        ) : (
          NotSet
        ),
      // Default overrides
      ...suggestedEntityProps,
      // Local overrides
      ...componentProps,
    };
  }, [componentProps, entity, entityTypeId, suggestedEntityProps]);

  const { canRead } = useEntityPermissions({ entityTypeId });

  if (!entity) return null;
  if (!canRead(entity)) return NotAllowed;
  return <ContentRow {...defaultComponentProps} />;
};

export const useDefaultGenericEntityContentRow: ({
  entityTypeId,
  entity,
}: {
  entityTypeId: GenericEntityConstantsEntities;
  entity?: GenericEntity;
}) => Partial<ContentRowProps> = ({ entityTypeId, entity }) => {
  if (!entity) return {};
  switch (entityTypeId) {
    case "auditLogs":
      const eventInfo = eventIdToInfo((entity as any as AuditLog).eventId);
      return {
        label: <DateTimeRenderer date={(entity as any as AuditLog).timestamp} includeDate includeElapsed includeTime />,
        info: (
          <div className={`label label-soft-${eventInfo.color}`}>
            {!!eventInfo.icon && <LucideIcon name={eventInfo.icon} />} {eventInfo.label}
          </div>
        ),
        date: (
          <div className="flex align-center gap-5">
            by
            <GetPersons persons={(entity as any as AuditLog).user} createLinks />
          </div>
        ),
      };
    case "notebookEntries":
      return {
        label: getNotebookEntryStringLabel({ entry: entity as LabNotebookEntry }),
      };
    default:
      return {};
  }
};
